import {
  WS_EVENTS,
  type AgentStatusData,
  type HandoverData,
  type NotificationData,
  type SessionData,
  type VisitorData,
  type WebSocketCallbacks,
  type WebSocketMessage,
} from '../websocket-shared'
import { buildAgentDashboardChatUrl } from '@/lib/utils'

export const WebSocketReadyState = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const

export type WebSocketReadyState =
  (typeof WebSocketReadyState)[keyof typeof WebSocketReadyState]

export interface WebSocketServiceState {
  readyState: WebSocketReadyState
  isConnected: boolean
  connectionId: string | null
  lastMessage: MessageEvent<any> | null
  lastJsonMessage: WebSocketMessage | null
}

type CallbackRef = { current: WebSocketCallbacks }
type StateListener = (state: WebSocketServiceState) => void

const HEARTBEAT_INTERVAL = 30_000
const CLEANUP_DELAY = 2_000

function handleAgentStatusNotification(
  eventType: string,
  data: AgentStatusData
) {
  if (typeof window === 'undefined') {
    return
  }

  const { useToastNotificationStore } = require('@/lib/toast-notifications')
  const { addToast } = useToastNotificationStore.getState()

  if (eventType === WS_EVENTS.AGENT_ONLINE) {
    addToast({
      type: 'info',
      title: 'Agent Online',
      message: 'Agent is now available to handle sessions',
    })
  } else if (eventType === WS_EVENTS.AGENT_OFFLINE) {
    addToast({
      type: 'warning',
      title: 'Agent Offline',
      message: 'Agent has gone offline',
    })
  }
}

function handleSessionNotification(eventType: string, data: SessionData) {
  if (typeof window === 'undefined') {
    return
  }

  const { useToastNotificationStore } = require('@/lib/toast-notifications')
  const { addToast } = useToastNotificationStore.getState()

  switch (eventType) {
    case WS_EVENTS.SESSION_CREATED:
      addToast({
        type: 'info',
        title: 'New Session Created',
        message: `Session ${data.sessionId.slice(-8)} has been created`,
        actionUrl: buildAgentDashboardChatUrl(data.sessionId),
      })
      break
    case WS_EVENTS.SESSION_UPDATED:
      addToast({
        type: 'info',
        title: 'Session Updated',
        message: `Session ${data.sessionId.slice(-8)} has been updated`,
        actionUrl: buildAgentDashboardChatUrl(data.sessionId),
      })
      break
    case WS_EVENTS.SESSION_COMPLETED:
      addToast({
        type: 'success',
        title: 'Session Completed',
        message: `Session ${data.sessionId.slice(-8)} has been completed`,
      })
      break
  }
}

function handleHandoverNotification(eventType: string, data: HandoverData) {
  if (typeof window === 'undefined') {
    return
  }

  const { useToastNotificationStore } = require('@/lib/toast-notifications')
  const { addToast } = useToastNotificationStore.getState()

  switch (eventType) {
    case WS_EVENTS.HANDOVER_REQUESTED: {
      const reason = data.reason
        ? `Reason: ${data.reason}`
        : 'No reason provided'
      const enhancedMessage = `Handover requested for session ${data.sessionId.slice(-8)}. ${reason}`
      addToast({
        type: 'warning',
        title: 'Handover Request',
        message: enhancedMessage,
        urgent: data.priority === 'high' || data.priority === 'urgent',
        actionUrl: buildAgentDashboardChatUrl(data.sessionId),
      })
      break
    }
    case WS_EVENTS.HANDOVER_ASSIGNED:
      addToast({
        type: 'info',
        title: 'Handover Assigned',
        message: `You have been assigned to handle session ${data.sessionId.slice(-8)}`,
        actionUrl: buildAgentDashboardChatUrl(data.sessionId),
      })
      break
    case WS_EVENTS.HANDOVER_COMPLETED:
      addToast({
        type: 'success',
        title: 'Handover Completed',
        message: `Session ${data.sessionId.slice(-8)} handover has been completed`,
      })
      break
  }
}

function handleDirectNotification(data: NotificationData) {
  if (typeof window === 'undefined') {
    return
  }

  const { useToastNotificationStore } = require('@/lib/toast-notifications')
  const { addToast } = useToastNotificationStore.getState()

  addToast({
    type: data.type,
    title: data.title,
    message: data.message,
    urgent: data.urgent,
    actionUrl: data.actionUrl,
  })
}

class WebSocketConnection {
  private socket: WebSocket | null = null
  private subscribers = new Map<
    symbol,
    { callbacksRef: CallbackRef; setState: StateListener }
  >()
  private jsonQueue: WebSocketMessage[] = []
  private rawQueue: (
    | string
    | ArrayBuffer
    | SharedArrayBuffer
    | Blob
    | ArrayBufferView
  )[] = []
  private reconnectAttempts = 0
  private forcedClose = false
  private shouldReconnect = true
  private reconnectTimer: number | null = null
  private cleanupTimer: number | null = null
  private heartbeatTimer: number | null = null
  private state: WebSocketServiceState = {
    readyState: WebSocketReadyState.CLOSED,
    isConnected: false,
    connectionId: null,
    lastMessage: null,
    lastJsonMessage: null,
  }

  constructor(private readonly url: string) {}

  getState(): WebSocketServiceState {
    return { ...this.state }
  }

  register(callbacksRef: CallbackRef, setState: StateListener): () => void {
    const id = Symbol(this.url)
    this.subscribers.set(id, { callbacksRef, setState })
    setState({ ...this.state })
    this.clearCleanupTimer()

    if (!this.socket || this.state.readyState === WebSocketReadyState.CLOSED) {
      this.forcedClose = false
      this.shouldReconnect = true
      this.connect()
    }

    return () => {
      this.subscribers.delete(id)
      if (this.subscribers.size === 0) {
        this.scheduleCleanup()
      }
    }
  }

  sendJson(message: WebSocketMessage) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message))
      return
    }

    this.jsonQueue.push(message)
    this.connect()
  }

  sendRaw(
    message: string | ArrayBuffer | SharedArrayBuffer | Blob | ArrayBufferView,
    keep = false
  ) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(message)
      if (!keep) {
        return
      }
    }

    if (keep) {
      this.rawQueue.push(message)
    }
    this.connect()
  }

  disconnect(code = 1000, reason = 'Client disconnect') {
    this.shouldReconnect = false
    this.forcedClose = true
    this.clearReconnectTimer()
    this.stopHeartbeat()

    if (this.socket && this.socket.readyState < WebSocketReadyState.CLOSED) {
      try {
        this.socket.close(code, reason)
      } catch {
        // ignore errors from closing an already closed socket
      }
    }

    this.cleanupSocket()
    this.updateState({
      readyState: WebSocketReadyState.CLOSED,
      isConnected: false,
      connectionId: null,
      lastMessage: null,
      lastJsonMessage: null,
    })
  }

  reconnect() {
    this.forcedClose = false
    this.shouldReconnect = true
    this.clearReconnectTimer()
    this.connect()
  }

  release() {
    if (this.subscribers.size > 0) {
      return
    }

    this.disconnect(1000, 'No active subscribers')
  }

  registerAgent(agentId: number, sessionId?: string) {
    if (!agentId || Number.isNaN(agentId) || agentId <= 0) {
      console.error(
        'âŒ [WebSocket] Invalid agentId provided:',
        agentId,
        'type:',
        typeof agentId
      )
      return
    }

    this.sendJson({
      type: WS_EVENTS.REGISTER,
      data: {
        connectionType: 'agent',
        agentId: Number(agentId),
        sessionId,
        connectionId: this.state.connectionId,
      },
    })
  }

  registerUser(sessionId: string) {
    if (!sessionId) {
      console.error(
        'âŒ [WebSocket] Cannot register user: sessionId is required'
      )
      return
    }

    this.sendJson({
      type: WS_EVENTS.REGISTER,
      data: {
        connectionType: 'user',
        sessionId,
        connectionId: this.state.connectionId,
      },
    })
  }

  updateAgentStatus(isOnline: boolean) {
    this.sendJson({
      type: WS_EVENTS.AGENT_STATUS_UPDATE,
      data: {
        isOnline,
        timestamp: new Date().toISOString(),
      },
    })
  }

  requestHandover(sessionId: string, reason?: string, priority = 'normal') {
    if (!sessionId) {
      console.error('âŒ [WebSocket] Cannot request handover without sessionId')
      return
    }

    this.sendJson({
      type: WS_EVENTS.HANDOVER_REQUESTED,
      data: {
        sessionId,
        reason,
        priority,
        timestamp: new Date().toISOString(),
      },
    })
  }

  private connect() {
    if (typeof window === 'undefined') {
      return
    }

    if (this.socket && this.socket.readyState <= WebSocket.OPEN) {
      return
    }

    try {
      this.socket = new WebSocket(this.url)
    } catch (error) {
      console.error('[WebSocket] Failed to initialize socket', error)
      this.scheduleReconnect()
      return
    }

    this.updateState({
      readyState: WebSocketReadyState.CONNECTING,
      isConnected: false,
    })

    this.socket.addEventListener('open', this.handleOpen)
    this.socket.addEventListener('close', this.handleClose)
    this.socket.addEventListener('error', this.handleError)
    this.socket.addEventListener('message', this.handleMessage)
  }

  private handleOpen = () => {
    this.reconnectAttempts = 0
    this.forcedClose = false
    this.clearReconnectTimer()
    this.updateState({
      readyState: WebSocketReadyState.OPEN,
      isConnected: true,
    })
    this.flushQueues()
    this.notifyCallbacks(callbacks => callbacks.onConnect?.())
    this.startHeartbeat()
  }

  private handleClose = () => {
    this.stopHeartbeat()

    this.updateState({
      readyState: WebSocketReadyState.CLOSED,
      isConnected: false,
      connectionId: null,
    })

    this.notifyCallbacks(callbacks => callbacks.onDisconnect?.())
    this.cleanupSocket()

    if (
      this.shouldReconnect &&
      !this.forcedClose &&
      this.subscribers.size > 0
    ) {
      this.scheduleReconnect()
    }
  }

  private handleError = (event: Event) => {
    this.notifyCallbacks(callbacks => callbacks.onError?.(event))
  }

  private handleMessage = (event: MessageEvent<any>) => {
    this.updateState({
      lastMessage: event,
    })

    let parsed: WebSocketMessage | null = null

    try {
      if (typeof event.data === 'string') {
        parsed = JSON.parse(event.data) as WebSocketMessage
      } else if (event.data) {
        parsed = event.data as WebSocketMessage
      }
    } catch (error) {
      console.error('[WebSocket] Failed to parse message', error, event.data)
      return
    }

    if (!parsed || typeof parsed !== 'object') {
      return
    }

    this.updateState({
      lastJsonMessage: parsed,
    })

    this.notifyCallbacks(callbacks => callbacks.onMessage?.(parsed!))
    this.routeMessage(parsed)
  }

  private routeMessage(message: WebSocketMessage) {
    switch (message.type) {
      case WS_EVENTS.AGENT_STATUS_UPDATE:
      case WS_EVENTS.AGENT_ONLINE:
      case WS_EVENTS.AGENT_OFFLINE: {
        const data = message.data as AgentStatusData
        this.notifyCallbacks(callbacks => callbacks.onAgentStatusUpdate?.(data))
        handleAgentStatusNotification(message.type, data)
        break
      }
      case WS_EVENTS.SESSION_ASSIGNED:
        this.notifyCallbacks(callbacks =>
          callbacks.onSessionAssigned?.(
            message.data as {
              sessionId: string
              agentId: number
              agentName: string
              timestamp: string
            }
          )
        )
        break
      case WS_EVENTS.SESSION_CREATED:
      case WS_EVENTS.SESSION_UPDATED:
      case WS_EVENTS.SESSION_ENDED:
      case WS_EVENTS.SESSION_MESSAGE:
      case WS_EVENTS.SESSION_COMPLETED: {
        const data = message.data as SessionData
        this.notifyCallbacks(callbacks => callbacks.onSessionUpdate?.(data))
        break
      }
      case WS_EVENTS.SESSION_TAKEN: {
        const data = message.data as { sessionId: string; agentId: number }
        this.notifyCallbacks(callbacks => callbacks.onSessionTaken?.(data))
        if (typeof window !== 'undefined') {
          try {
            const { useNotificationStore } = require('@/lib/notifications')
            const { fetchNotifications, fetchUnreadCount } =
              useNotificationStore.getState()
            // Refresh notifications from server to get updated state
            fetchNotifications({ includeRead: true, includeDismissed: true })
            fetchUnreadCount()
          } catch (err) {
            console.error('[WebSocket] Failed to refresh notifications', err)
          }
        }
        break
      }
      case WS_EVENTS.HANDOVER_REQUESTED:
      case WS_EVENTS.HANDOVER_ASSIGNED:
      case WS_EVENTS.HANDOVER_COMPLETED: {
        const data = message.data as HandoverData
        this.notifyCallbacks(callbacks => callbacks.onHandoverRequest?.(data))
        handleHandoverNotification(message.type, data)
        break
      }
      case 'transfer_request_received':
        this.notifyCallbacks(callbacks =>
          callbacks.onTransferRequestReceived?.(message.data as any)
        )
        break
      case 'transfer_request_sent':
        this.notifyCallbacks(callbacks =>
          callbacks.onTransferRequestSent?.(message.data as any)
        )
        break
      case WS_EVENTS.NOTIFICATION:
      case WS_EVENTS.URGENT_NOTIFICATION: {
        const data = message.data as NotificationData
        this.notifyCallbacks(callbacks => callbacks.onNotification?.(data))
        handleDirectNotification(data)
        break
      }
      case 'visitor_connected':
        this.notifyCallbacks(callbacks =>
          callbacks.onVisitorConnected?.(message.data as VisitorData)
        )
        break
      case 'visitor_disconnected':
        this.notifyCallbacks(callbacks =>
          callbacks.onVisitorDisconnected?.(message.data as VisitorData)
        )
        break
      case WS_EVENTS.CONNECTION_ESTABLISHED: {
        const connectionData =
          (message.data as { connectionId?: string } | undefined) || {}
        this.updateState({
          connectionId: connectionData.connectionId || null,
        })
        break
      }
      case 'registration_confirmed': {
        const data = message.data as { connectionId?: string } | undefined
        if (data?.connectionId) {
          this.updateState({ connectionId: data.connectionId })
        }
        break
      }
      case WS_EVENTS.PING:
        this.sendJson({
          type: WS_EVENTS.PONG,
          data: {
            timestamp: new Date().toISOString(),
          },
        })
        break
      case WS_EVENTS.PONG:
        break
      case WS_EVENTS.TICKET_CREATED:
      case WS_EVENTS.TICKET_REPLY_CREATED:
      case WS_EVENTS.TICKET_STATUS_UPDATED:
      case WS_EVENTS.TICKET_ASSIGNED:
      case WS_EVENTS.TICKET_UPDATED: {
        // Handle ticket events for real-time updates
        if (typeof window !== 'undefined') {
          try {
            // Dynamically import to get the global QueryClient instance
            import('@tanstack/react-query').then(({ useQueryClient }) => {
              const ticketData = message.data as { ticketId: number }

              // Dispatch custom event that React components can listen to
              const event = new CustomEvent('ticket-update', {
                detail: {
                  type: message.type,
                  ticketId: ticketData.ticketId,
                  data: message.data,
                },
              })
              window.dispatchEvent(event)

              console.log(
                `📧 Ticket ${message.type} event broadcast for ticket #${ticketData.ticketId}`
              )
            })
          } catch (err) {
            console.error('[WebSocket] Failed to process ticket event', err)
          }
        }
        break
      }
      default:
        console.log('Unhandled WebSocket message type:', message.type)
    }
  }

  private flushQueues() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return
    }

    while (this.jsonQueue.length) {
      const payload = this.jsonQueue.shift()
      if (payload) {
        this.socket.send(JSON.stringify(payload))
      }
    }

    while (this.rawQueue.length) {
      const payload = this.rawQueue.shift()
      if (payload) {
        this.socket.send(payload)
      }
    }
  }

  private scheduleReconnect() {
    if (
      !this.shouldReconnect ||
      this.forcedClose ||
      this.subscribers.size === 0
    ) {
      return
    }

    const attempt = this.reconnectAttempts + 1
    const delay = Math.min(2000 * Math.pow(2, Math.max(attempt - 1, 0)), 30000)
    const jitter = Math.random() * 1000

    this.clearReconnectTimer()
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectAttempts = attempt
      this.connect()
    }, delay + jitter)
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private scheduleCleanup() {
    if (this.cleanupTimer) {
      return
    }

    this.cleanupTimer = window.setTimeout(() => {
      this.cleanupTimer = null
      this.shouldReconnect = false
      this.disconnect(1000, 'No active subscribers')
    }, CLEANUP_DELAY)
  }

  private clearCleanupTimer() {
    if (this.cleanupTimer) {
      window.clearTimeout(this.cleanupTimer)
      this.cleanupTimer = null
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = window.setInterval(() => {
      this.sendJson({
        type: WS_EVENTS.PING,
        data: {
          timestamp: new Date().toISOString(),
        },
      })
    }, HEARTBEAT_INTERVAL)
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private cleanupSocket() {
    if (!this.socket) {
      return
    }

    this.socket.removeEventListener('open', this.handleOpen)
    this.socket.removeEventListener('close', this.handleClose)
    this.socket.removeEventListener('error', this.handleError)
    this.socket.removeEventListener('message', this.handleMessage)
    this.socket = null
  }

  private updateState(partial: Partial<WebSocketServiceState>) {
    this.state = {
      ...this.state,
      ...partial,
    }
    this.emitState()
  }

  private emitState() {
    const snapshot = { ...this.state }
    this.subscribers.forEach(({ setState }) => {
      setState(snapshot)
    })
  }

  private notifyCallbacks(handler: (callbacks: WebSocketCallbacks) => void) {
    this.subscribers.forEach(({ callbacksRef }) => {
      const callbacks = callbacksRef.current
      if (!callbacks) {
        return
      }

      try {
        handler(callbacks)
      } catch (error) {
        console.error('[WebSocket] Callback handler failed', error)
      }
    })
  }
}

export class WebSocketService {
  private static instance: WebSocketService
  private connections = new Map<string, WebSocketConnection>()

  private constructor() {}

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService()
    }
    return WebSocketService.instance
  }

  register(
    url: string,
    callbacksRef: CallbackRef,
    setState: StateListener
  ): () => void {
    const connection = this.getConnection(url)
    return connection.register(callbacksRef, setState)
  }

  getState(url: string): WebSocketServiceState {
    return (
      this.connections.get(url)?.getState() ?? {
        readyState: WebSocketReadyState.CLOSED,
        isConnected: false,
        connectionId: null,
        lastMessage: null,
        lastJsonMessage: null,
      }
    )
  }

  sendJsonMessage(url: string, message: WebSocketMessage) {
    const connection = this.getConnection(url)
    connection.sendJson(message)
  }

  sendRawMessage(
    url: string,
    message: string | ArrayBuffer | SharedArrayBuffer | Blob | ArrayBufferView,
    keep = false
  ) {
    const connection = this.getConnection(url)
    connection.sendRaw(message, keep)
  }

  disconnect(url: string, code?: number, reason?: string) {
    const connection = this.connections.get(url)
    if (!connection) {
      return
    }
    connection.disconnect(code, reason)
  }

  reconnect(url: string) {
    const connection = this.connections.get(url)
    if (!connection) {
      return
    }
    connection.reconnect()
  }

  release(url: string) {
    const connection = this.connections.get(url)
    if (!connection) {
      return
    }

    connection.release()
    if (connection.getState().readyState === WebSocketReadyState.CLOSED) {
      this.connections.delete(url)
    }
  }

  registerAgent(url: string, agentId: number, sessionId?: string) {
    const connection = this.getConnection(url)
    connection.registerAgent(agentId, sessionId)
  }

  registerUser(url: string, sessionId: string) {
    const connection = this.getConnection(url)
    connection.registerUser(sessionId)
  }

  updateAgentStatus(url: string, isOnline: boolean) {
    const connection = this.getConnection(url)
    connection.updateAgentStatus(isOnline)
  }

  requestHandover(
    url: string,
    sessionId: string,
    reason?: string,
    priority = 'normal'
  ) {
    const connection = this.getConnection(url)
    connection.requestHandover(sessionId, reason, priority)
  }

  private getConnection(url: string): WebSocketConnection {
    const existing = this.connections.get(url)
    if (existing) {
      return existing
    }

    const connection = new WebSocketConnection(url)
    this.connections.set(url, connection)
    return connection
  }
}
